import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Award } from 'lucide-react';
import { getBadgeSvg } from './badgeConfig';
import { fireConfettiCelebration } from '../../utils/celebration';

const rarityGradient = {
  common: 'from-gray-500 to-gray-600',
  uncommon: 'from-green-500 to-green-600',
  rare: 'from-blue-500 to-blue-600',
  epic: 'from-purple-500 to-purple-600',
  legendary: 'from-yellow-400 via-yellow-500 to-orange-500',
};

/**
 * Acknowledges a badge the user just earned. Badge acquisition is otherwise
 * completely silent -- this is the only "you made progress" moment in the
 * whole gamification system.
 */
const BadgeEarnedToast = ({ badge, onDismiss }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = requestAnimationFrame(() => {
      setVisible(true);
      fireConfettiCelebration();
    });
    const hideTimer = setTimeout(() => setVisible(false), 6000);
    const removeTimer = setTimeout(onDismiss, 6400);
    return () => {
      cancelAnimationFrame(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, [onDismiss]);

  if (!badge) return null;

  const rarity = badge.rarity?.toLowerCase() || 'common';
  const gradient = rarityGradient[rarity] || rarityGradient.common;
  const svgPath = getBadgeSvg(badge);

  return (
    <div
      className={`fixed top-20 right-4 z-[100] w-[min(22rem,calc(100vw-2rem))] transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
      }`}
      role="status"
      aria-live="polite"
    >
      <div className={`relative overflow-hidden rounded-2xl border-2 border-transparent bg-gradient-to-br ${gradient} shadow-xl p-4`}>
        <button
          onClick={() => setVisible(false)}
          className="absolute top-2 right-2 text-white/70 hover:text-white"
          aria-label={t('profileWidgets.badgeEarnedToast.dismissAria')}
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm flex-shrink-0">
            {svgPath ? (
              <img src={svgPath} alt="" className="w-9 h-9 object-contain" />
            ) : (
              <Award className="w-9 h-9 text-white" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">{t('profileWidgets.badgeEarnedToast.achievementUnlocked')}</p>
            <p className="text-sm font-bold text-white truncate">{badge.name}</p>
            {badge.points > 0 && (
              <p className="text-xs text-white/80">+{badge.points} {t('profileWidgets.badgeCard.xp')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeEarnedToast;
